// frontend/src/hooks/useResponses.ts - NEW RESPONSE-SPECIFIC HOOKS
// Entity-specific hooks for responses separated from core useApi.ts
// Insert this as a new file: useResponses.ts

import { useCallback } from 'react';
import apiClient from '@/api';
import { useAuthStore } from '@/store/auth';
import { 
  useApi, 
  useMutation, 
  validateObjectId, 
  validateObjectIds, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,
  
  // Response types
  Response,
  ResponseQueryParams,
  ResponseCreateRequest,
  ResponseUpdateRequest,
  
  // Query parameter types
  QueryParams
} from '@/api/types';

// ===== LOCAL INTERFACES =====

/**
 * Extended response creation data with additional features
 */
interface ExtendedResponseCreateRequest extends ResponseCreateRequest {
  attachments?: string[];
  tags?: string[];
  customFields?: Array<{
    name: string;
    value: string;
    type: 'text' | 'number' | 'date' | 'boolean' | 'url';
  }>;
}

/**
 * Extended response update data
 */
interface ExtendedResponseUpdateRequest extends ResponseUpdateRequest {
  preserveHistory?: boolean;
  updateReason?: string;
}

/**
 * Response comment interface (with populated user)
 */
export interface ResponseCommentData {
  _id: string;
  content: string;
  userId?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  stageName?: string;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Response comment interface (basic)
 */
interface ResponseComment {
  _id: string;
  id?: string;
  content: string;
  userId: string;
  username: string;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Response workflow action interface
 */
interface ResponseWorkflowAction {
  id: string;
  action: 'approve' | 'reject' | 'send' | 'recall' | 'schedule';
  comment?: string;
  reason?: string;
  scheduledFor?: string;
}

// ===== RESPONSE QUERY HOOKS =====

/**
 * Fetch paginated list of responses with comprehensive filtering
 */
export const useResponses = (params?: ResponseQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Response>> => {

      const response = await apiClient.responses.list(params);

      // CUSTOM TRANSFORMATION - Backend returns {responses: [...]} but we need {data: [...]}
      if (response.responses && Array.isArray(response.responses)) {

        const transformedResponse = {
          data: response.responses,
          pagination: response.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalItems: response.responses.length,
            itemsPerPage: response.responses.length,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: response.summary?.total || response.responses.length,
          totalPages: response.pagination?.pages || 1,
          page: response.pagination?.current || 1,
          limit: response.pagination?.limit || response.responses.length
        };

        return normalizePaginatedResponse<Response>(transformedResponse);
      }

      return normalizePaginatedResponse<Response>(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      retryCount: 2,
      retryDelay: 1000,
      rateLimitKey: 'responses_list',
      ...options
    }
  );
};

/**
 * Fetch single response by ID with comprehensive details
 */
export const useResponse = (id?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<Response> => {
      validateObjectId(id, 'Response ID');
      
      
      const response = await apiClient.responses.get(id!);
      
      return normalizeSingleResponse<Response>(response);
    },
    {
      immediate: !!id,
      rateLimitKey: `response_${id}`,
      ...options
    }
  );
};

/**
 * Search responses with advanced filtering
 */
export const useSearchResponses = (query?: string, filters?: any, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Response>> => {
      if (!query?.trim()) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      
      // Check if responses.search method exists, otherwise use list with search param
      const response = (apiClient.responses as any).search
        ? await (apiClient.responses as any).search(query, filters)
        : await apiClient.responses.list({ ...filters, search: query });
      
      
      return normalizePaginatedResponse<Response>(response);
    },
    {
      immediate: !!query?.trim(),
      dependencies: [query, JSON.stringify(filters || {})],
      debounce: 500,
      rateLimitKey: 'responses_search',
      ...options
    }
  );
};

// ===== RESPONSE COMMENTS HOOKS =====

/**
 * Get response comments
 */
export const useResponseComments = (responseId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<{ comments: ResponseComment[] }> => {
      validateObjectId(responseId, 'Response ID');

      const raw = await apiClient.responses.getComments(responseId!) as any;

      // handleResponse may transform { comments, pagination } → { data, pagination }
      // Page expects { comments: [...] }
      return {
        comments: raw.comments || raw.data || (Array.isArray(raw) ? raw : [])
      };
    },
    {
      immediate: !!responseId,
      rateLimitKey: `response_comments_${responseId}`,
      ...options
    }
  );
};

/**
 * Get response history
 */
export const useResponseHistory = (responseId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any[]> => {
      validateObjectId(responseId, 'Response ID');
      
      
      const response = await apiClient.responses.getHistory(responseId!);
      
      return normalizeSingleResponse<any[]>(response);
    },
    {
      immediate: !!responseId,
      rateLimitKey: `response_history_${responseId}`,
      ...options
    }
  );
};

/**
 * Get response version
 */
export const useResponseVersion = (responseId?: string, version?: number, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<Response> => {
      validateObjectId(responseId, 'Response ID');
      
      if (!version || version < 1) {
        throw new Error('Valid version number is required');
      }
      
      
      const response = await apiClient.responses.getVersion(responseId!, version);
      
      return normalizeSingleResponse<Response>(response);
    },
    {
      immediate: !!(responseId && version),
      dependencies: [responseId, version],
      rateLimitKey: `response_version_${responseId}_${version}`,
      ...options
    }
  );
};

// ===== RESPONSE ANALYTICS HOOKS =====

/**
 * Get response analytics
 */
export const useResponseAnalytics = (params?: any, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      
      const response = await apiClient.responses.getAnalytics(params);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      rateLimitKey: 'response_analytics',
      ...options
    }
  );
};

// ===== RESPONSE MUTATION HOOKS =====

/**
 * Create response with enhanced validation
 */
export const useCreateResponse = (options?: UseMutationOptions<Response, ExtendedResponseCreateRequest>) => {
  return useMutation(
    async (data: ExtendedResponseCreateRequest): Promise<Response> => {
      
      // Enhanced client-side validation
      if (!data.mailBody || typeof data.mailBody !== 'string' || data.mailBody.trim().length === 0) {
        throw new Error('Response content is required');
      }
      
      if (!data.mailSubject || typeof data.mailSubject !== 'string' || data.mailSubject.trim().length === 0) {
        throw new Error('Response subject is required');
      }

      // Validate workflow ID if provided (optional for manual responses)
      if (data.workflowId) {
        if (typeof data.workflowId !== 'string') {
          throw new Error('Workflow ID must be a string');
        }
        validateObjectId(data.workflowId, 'Workflow ID');
      }
      
      // Validate correspondence ID if provided
      if (data.correspondenceId) {
        validateObjectId(data.correspondenceId, 'Correspondence ID');
      }
      
      // Validate source IDs if provided
      if (data.sourceIds && Array.isArray(data.sourceIds)) {
        data.sourceIds.forEach((sourceId: string, index: number) => {
          validateObjectId(sourceId, `Source ${index + 1} ID`);
        });
      }
      
      // Validate attachments if provided
      if (data.attachments && Array.isArray(data.attachments)) {
        data.attachments.forEach((attachmentId: string, index: number) => {
          validateObjectId(attachmentId, `Attachment ${index + 1} ID`);
        });
      }
      
      // Remove extended fields for API call
      const { attachments, tags, customFields, ...apiData } = data;
      
      const response = await apiClient.responses.create(apiData);
      
      return normalizeSingleResponse<Response>(response);
    }, 
    options
  );
};

/**
 * Update response with validation
 */
export const useUpdateResponse = (options?: UseMutationOptions<Response, { id: string; data: ExtendedResponseUpdateRequest }>) => {
  return useMutation(
    async ({ id, data }): Promise<Response> => {
      validateObjectId(id, 'Response ID');
      
      
      // Validate content if provided
      if (data.mailBody !== undefined) {
        if (!data.mailBody || typeof data.mailBody !== 'string' || data.mailBody.trim().length === 0) {
          throw new Error('Response content cannot be empty');
        }
      }
      
      // Validate subject if provided
      if (data.mailSubject !== undefined) {
        if (!data.mailSubject || typeof data.mailSubject !== 'string' || data.mailSubject.trim().length === 0) {
          throw new Error('Response subject cannot be empty');
        }
      }
      
      // Validate source IDs if provided
      if (data.sourceIds && Array.isArray(data.sourceIds)) {
        data.sourceIds.forEach((sourceId: string, index: number) => {
          validateObjectId(sourceId, `Source ${index + 1} ID`);
        });
      }
      
      // Remove extended fields for API call
      const { preserveHistory, updateReason, ...apiData } = data;
      
      const response = await apiClient.responses.update(id, apiData);
      
      return normalizeSingleResponse<Response>(response);
    },
    options
  );
};

/**
 * Delete response
 */
export const useDeleteResponse = (options?: UseMutationOptions<void, string>) => {
  return useMutation(
    async (id: string): Promise<void> => {
      validateObjectId(id, 'Response ID');
      
      
      const response = await apiClient.responses.delete(id);
      
      return normalizeSingleResponse<void>(response);
    },
    options
  );
};

// ===== RESPONSE WORKFLOW ACTION HOOKS =====

/**
 * Approve response
 */
export const useApproveResponse = (options?: UseMutationOptions<Response, { id: string; comment?: string; stageComplete?: boolean }>) => {
  return useMutation(
    async ({ id, comment, stageComplete }): Promise<Response> => {
      validateObjectId(id, 'Response ID');

      // Use default comment if none provided
      const approvalComment = (comment && comment.trim().length > 0) ? comment.trim() : 'Approved';

      const response = await apiClient.responses.approve(id, approvalComment, stageComplete);

      return normalizeSingleResponse<Response>(response);
    },
    options
  );
};

/**
 * Reject response
 */
export const useRejectResponse = (options?: UseMutationOptions<Response, { id: string; comment: string }>) => {
  return useMutation(
    async ({ id, comment }): Promise<Response> => {
      validateObjectId(id, 'Response ID');
      
      if (!comment || typeof comment !== 'string' || comment.trim().length === 0) {
        throw new Error('Rejection comment is required');
      }
      
      
      const response = await apiClient.responses.reject(id, comment);
      
      return normalizeSingleResponse<Response>(response);
    },
    options
  );
};

/**
 * Send response
 */
export const useSendResponse = (options?: UseMutationOptions<Response, string>) => {
  return useMutation(
    async (id: string): Promise<Response> => {
      validateObjectId(id, 'Response ID');
      
      
      const response = await apiClient.responses.send(id);
      
      return normalizeSingleResponse<Response>(response);
    },
    options
  );
};

/**
 * Recall response
 */
export const useRecallResponse = (options?: UseMutationOptions<Response, { id: string; reason: string }>) => {
  return useMutation(
    async ({ id, reason }): Promise<Response> => {
      validateObjectId(id, 'Response ID');
      
      if (!reason || typeof reason !== 'string' || reason.trim().length === 0) {
        throw new Error('Recall reason is required');
      }
      
      
      const response = await apiClient.responses.recall(id, reason);
      
      return normalizeSingleResponse<Response>(response);
    },
    options
  );
};

/**
 * Schedule response
 */
export const useScheduleResponse = (options?: UseMutationOptions<Response, { id: string; scheduledFor: string }>) => {
  return useMutation(
    async ({ id, scheduledFor }): Promise<Response> => {
      validateObjectId(id, 'Response ID');
      
      if (!scheduledFor || typeof scheduledFor !== 'string') {
        throw new Error('Scheduled date/time is required');
      }
      
      // Validate date format (basic ISO date validation)
      const scheduledDate = new Date(scheduledFor);
      if (isNaN(scheduledDate.getTime())) {
        throw new Error('Invalid scheduled date format');
      }
      
      // Check if scheduled time is in the future
      if (scheduledDate <= new Date()) {
        throw new Error('Scheduled time must be in the future');
      }
      
      
      const response = await apiClient.responses.schedule(id, scheduledFor);
      
      return normalizeSingleResponse<Response>(response);
    },
    options
  );
};

// ===== RESPONSE COMMENT MUTATION HOOKS =====

/**
 * Add comment to response
 */
export const useAddResponseComment = (options?: UseMutationOptions<ResponseComment, { id: string; comment: string; stageName?: string }>) => {
  return useMutation(
    async ({ id, comment, stageName }): Promise<ResponseComment> => {
      validateObjectId(id, 'Response ID');

      if (!comment || typeof comment !== 'string' || comment.trim().length === 0) {
        throw new Error('Comment content is required');
      }

      const response = await apiClient.responses.addComment(id, comment, stageName);

      return normalizeSingleResponse<ResponseComment>(response);
    },
    options
  );
};

/**
 * Update response comment
 */
export const useUpdateResponseComment = (options?: UseMutationOptions<ResponseComment, { 
  id: string; 
  commentId: string; 
  content: string 
}>) => {
  return useMutation(
    async ({ id, commentId, content }): Promise<ResponseComment> => {
      validateObjectId(id, 'Response ID');
      validateObjectId(commentId, 'Comment ID');
      
      if (!content || typeof content !== 'string' || content.trim().length === 0) {
        throw new Error('Comment content is required');
      }
      
      
      const response = await apiClient.responses.updateComment(id, commentId, content);
      
      return normalizeSingleResponse<ResponseComment>(response);
    },
    options
  );
};

/**
 * Delete response comment
 */
export const useDeleteResponseComment = (options?: UseMutationOptions<void, { id: string; commentId: string }>) => {
  return useMutation(
    async ({ id, commentId }): Promise<void> => {
      validateObjectId(id, 'Response ID');
      validateObjectId(commentId, 'Comment ID');
      
      
      const response = await apiClient.responses.deleteComment(id, commentId);
      
      return normalizeSingleResponse<void>(response);
    },
    options
  );
};

// ===== LEGACY COMPATIBILITY HOOKS =====

/**
 * Submit response (alias for send)
 */
export const useSubmitResponse = (options?: UseMutationOptions<Response, string>) => {
  return useMutation(
    async (id: string): Promise<Response> => {
      validateObjectId(id, 'Response ID');


      const response = await apiClient.responses.send(id);

      return normalizeSingleResponse<Response>(response);
    },
    options
  );
};

// ===== NEW ENDPOINTS FOR RESPONSES REDESIGN =====

/**
 * Interface for full response details including source correspondences
 */
export interface FullResponseDetails {
  response: Response;
  sourceCorrespondences: Array<{
    _id: string;
    subject?: string;
    from?: string;
    to?: string;
    date?: string;
    body?: string;
    type?: string;
    closureType?: 'full' | 'partial';
    isPrimary?: boolean;
    attachments?: Array<{
      _id: string;
      filename: string;
      s3Key?: string;
      s3Url?: string;
      extractedContent?: string;
      contentType?: string;
      size?: number;
    }>;
  }>;
  workflow?: {
    _id: string;
    name: string;
    description?: string;
    stages?: Array<{
      stageName: string;
      approvalGroups?: Array<{
        _id: string;
        name: string;
        members?: string[];
      }>;
    }>;
  } | null;
  currentStageApprovers: Array<{
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }>;
  permissions: {
    canEdit: boolean;
    canComment: boolean;
    canApprove: boolean;
    canReject: boolean;
    canSend: boolean;
    canDelete: boolean;
    canViewHistory: boolean;
  };
}

/**
 * Fetch response with full details including source correspondences and permissions
 */
export const useResponseFull = (id?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<FullResponseDetails> => {
      validateObjectId(id, 'Response ID');


      const raw = await apiClient.get<any>(`/responses/${id}/full`);

      // Backend returns { response: { ...doc, permissions, workflow, sourceCorrespondences, ... }, metadata }
      // Frontend FullResponseDetails expects { response, permissions, sourceCorrespondences, workflow, currentStageApprovers }
      const resp = raw.response || raw;

      // Map sourceCorrespondences: backend has [{ correspondenceId: {...}, isPrimary, ... }]
      // Frontend panel expects flat [{ _id, subject, from, to, date, body, attachments, type }]
      // Also try sourceIds which are populated correspondence docs with nested emailId
      let mappedSources: any[] = [];
      if (resp.sourceCorrespondences?.length > 0) {
        mappedSources = resp.sourceCorrespondences
          .map((sc: any) => {
            const corr = sc.correspondenceId;
            if (!corr) return null;
            return {
              _id: corr._id,
              subject: corr.subject,
              from: corr.from,
              to: corr.to,
              date: corr.dateReceived,
              body: corr.body,
              type: corr.category || corr.direction,
              closureType: sc.closureType || 'full',
              isPrimary: sc.isPrimary || false,
              attachments: corr.attachments || []
            };
          })
          .filter(Boolean);
      } else if (resp.sourceIds?.length > 0) {
        mappedSources = resp.sourceIds.map((s: any) => ({
          _id: s._id,
          subject: s.subject,
          from: s.emailId?.from,
          to: s.emailId?.to,
          date: s.emailId?.date || s.dateReceived,
          body: s.emailId?.body,
          type: s.category || s.direction,
          attachments: s.emailId?.attachments || []
        }));
      }

      return {
        response: resp,
        sourceCorrespondences: mappedSources,
        workflow: resp.workflow || resp.workflowId || null,
        currentStageApprovers: resp.currentStageApprovers || [],
        permissions: resp.permissions || {
          canEdit: false,
          canComment: false,
          canApprove: false,
          canReject: false,
          canSend: false,
          canDelete: false,
          canViewHistory: false
        }
      };
    },
    {
      immediate: !!id,
      rateLimitKey: `response_full_${id}`,
      ...options
    }
  );
};

/**
 * Interface for response versions
 */
export interface ResponseVersionDetails {
  versionNumber: number;
  content?: {
    subject?: string;
    body?: string;
  };
  attachments?: string[];
  createdBy?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  createdAt: string;
  comment?: string;
}

export interface ResponseVersionsResponse {
  versions: ResponseVersionDetails[];
  currentVersion: number;
  total: number;
}

/**
 * Fetch all versions for a response
 */
export const useResponseVersions = (responseId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<ResponseVersionsResponse> => {
      validateObjectId(responseId, 'Response ID');


      const raw = await apiClient.get<any>(`/responses/${responseId}/versions`);

      // handleResponse may transform { versions, pagination, currentVersion } → { data, pagination }
      // Normalize back to expected shape
      return {
        versions: raw.versions || raw.data || [],
        currentVersion: raw.currentVersion ?? 1,
        total: raw.total ?? raw.pagination?.total ?? 0
      };
    },
    {
      immediate: !!responseId,
      rateLimitKey: `response_versions_${responseId}`,
      ...options
    }
  );
};

/**
 * Interface for version comparison
 */
export interface VersionComparison {
  version1: ResponseVersionDetails;
  version2: ResponseVersionDetails;
  diff: {
    subject: {
      v1: string;
      v2: string;
      changed: boolean;
    };
    body: {
      v1: string;
      v2: string;
      changed: boolean;
    };
    attachments: {
      added: any[];
      removed: any[];
    };
  };
}

/**
 * Compare two versions
 */
export const useCompareVersions = (
  responseId?: string,
  v1?: number,
  v2?: number,
  options?: UseApiOptions
) => {
  return useApi(
    async (): Promise<VersionComparison> => {
      validateObjectId(responseId, 'Response ID');

      if (!v1 || !v2 || v1 < 1 || v2 < 1) {
        throw new Error('Valid version numbers are required');
      }


      const response = await apiClient.get<VersionComparison>(
        `/responses/${responseId}/versions/${v1}/compare/${v2}`
      );

      // apiClient.get() already extracts data via handleResponse, so return directly
      return response;
    },
    {
      immediate: !!responseId && !!v1 && !!v2,
      rateLimitKey: `response_compare_${responseId}_${v1}_${v2}`,
      ...options
    }
  );
};

/**
 * Interface for activity timeline
 */
export interface ResponseActivity {
  action: string;
  userId?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  timestamp: string;
  stageName?: string;
  comment?: string;
  metadata?: any;
  ipAddress?: string;
}

export interface ResponseActivityResponse {
  activities: ResponseActivity[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Fetch activity timeline for a response
 */
export const useResponseActivity = (
  responseId?: string,
  params?: { page?: number; limit?: number },
  options?: UseApiOptions
) => {
  return useApi(
    async (): Promise<ResponseActivityResponse> => {
      validateObjectId(responseId, 'Response ID');


      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());

      const url = `/responses/${responseId}/activity${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const raw = await apiClient.get<any>(url);

      // handleResponse may transform { activities, pagination } → { data, pagination }
      return {
        activities: raw.activities || raw.data || [],
        total: raw.total ?? raw.pagination?.totalItems ?? 0,
        page: raw.page ?? raw.pagination?.currentPage ?? 1,
        limit: raw.limit ?? raw.pagination?.itemsPerPage ?? 20,
        totalPages: raw.totalPages ?? raw.pagination?.totalPages ?? 0
      };
    },
    {
      immediate: !!responseId,
      dependencies: [responseId, JSON.stringify(params)],
      rateLimitKey: `response_activity_${responseId}`,
      ...options
    }
  );
};

